import React, { useEffect, useRef, useState } from "react";

const SHOW_MONTHS = [3]; // Nisan = 3 (0-indexed)
const SHOW_DAYS = [22, 23];
const AUTO_CLOSE_MS = 63_000;
const MAX_DAILY_SHOWS = 3;
const STORAGE_KEY = "holiday_popup_23nisan";

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function getShowCount(): number {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const { date, count } = JSON.parse(raw);
    if (date !== getTodayKey()) return 0;
    return count as number;
  } catch {
    return 0;
  }
}

function incrementShowCount() {
  try {
    const count = getShowCount() + 1;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ date: getTodayKey(), count }));
  } catch {}
}

const HolidayPopup: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [muted, setMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const now = new Date();
    if (!SHOW_MONTHS.includes(now.getMonth()) || !SHOW_DAYS.includes(now.getDate())) return;
    if (getShowCount() >= MAX_DAILY_SHOWS) return;

    incrementShowCount();
    setVisible(true);
    timerRef.current = setTimeout(() => setVisible(false), AUTO_CLOSE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = muted;
    if (visible) audioRef.current.play().catch(() => {});
  }, [visible, muted]);

  const close = () => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Arka plan overlay */}
      <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm" onClick={close} />

      {/* Popup kutusu */}
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto w-full"
          style={{ maxWidth: "720px" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Kapat butonu */}
          <button
            onClick={close}
            className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors text-sm font-bold"
            aria-label="Kapat"
          >
            ✕
          </button>

          {/* Görsel */}
          <img
            src="/images/23nisan-gorseli.png"
            alt="23 Nisan Ulusal Egemenlik ve Çocuk Bayramı"
            className="w-full object-cover"
            style={{ maxHeight: "460px" }}
          />

          {/* Alt alan */}
          <div className="px-8 py-5 text-center bg-gradient-to-b from-red-50 to-white">
            <h2 className="text-xl font-extrabold text-red-700 mb-1">23 Nisan Kutlu Olsun!</h2>
            <p className="text-gray-500 text-sm mb-4">
              Ulusal Egemenlik ve Çocuk Bayramı'nın 105. yılı kutlu olsun.
            </p>
            <button
              onClick={() => setMuted((m) => !m)}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors"
              style={{
                backgroundColor: muted ? "#e5e7eb" : "#dc2626",
                color: muted ? "#374151" : "#ffffff",
              }}
            >
              {muted ? "🔇 Sesi Aç" : "🔊 Sesi Kapat"}
            </button>
          </div>

          <audio ref={audioRef} src="/audio/23nisan-marsi.mp3" loop muted />
        </div>
      </div>
    </>
  );
};

export default HolidayPopup;
